import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Card, Icon, Title, Text } from '@nimbus-ds/components'
import { CheckCircleIcon, ExclamationCircleIcon, ExternalLinkIcon } from '@nimbus-ds/icons'

export function InstallSuccessPage() {
  const [searchParams] = useSearchParams()
  const [storeInfo, setStoreInfo] = useState<any>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    // Parse installation result from URL
    const error = searchParams.get('error')
    const storeId = searchParams.get('store_id')
    const storeName = searchParams.get('store_name')

    if (error) {
      setHasError(true)
      return
    }

    if (storeId && storeName) {
      setStoreInfo({
        store_id: storeId,
        store_name: decodeURIComponent(storeName)
      })
    }
  }, [searchParams])

  const handleLoginClick = () => {
    // Redirect to login page with user auth flow
    window.location.href = '/'
  }

  const handleManageApp = () => {
    // Redirect to TiendaNube admin panel
    window.open('https://tiendanube.com/admin/apps/', '_blank')
  }

  if (hasError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
        <div className="flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <Card.Header className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
              </div>
              <Title as="h3" className="text-red-900">Error de Instalación</Title>
              <Text className="text-red-700">
                No se pudo instalar TN-Xperiment en tu tienda
              </Text>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <Button
                  onClick={() => window.history.back()}
                  fullWidth
                >
                  Volver
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <Card.Header className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <Title as="h2" className="text-green-900">
              ¡Tienda conectada!
            </Title>
            <Text className="text-green-900">
              {storeInfo ? (
                <>
                  ECommerceLab Desk ya está conectado a <strong>{storeInfo.store_name}</strong>
                </>
              ) : (
                <>ECommerceLab Desk ya está conectado a tu tienda</>
              )}
            </Text>
          </Card.Header>

          <Card.Body>
            <div className="space-y-6">
              {/* Primary message for the merchant */}
              <div className="rounded-lg bg-green-50 p-5 text-center">
                <p className="text-green-900">
                  <strong>No necesitás hacer nada más.</strong>
                </p>
                <p className="text-sm text-green-800 mt-2">
                  Tu consultor de ECommerceLab gestionará los experimentos y la optimización
                  de esta tienda. Ya podés cerrar esta ventana.
                </p>
              </div>

              {/* What was installed */}
              <div className="rounded-lg bg-gray-50 p-4">
                <Title as="h3" className="mb-2">¿Qué se conectó?</Title>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <Text>Script de seguimiento de experimentos</Text>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <Text>Conexión para gestión de experimentos</Text>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <Text>Analytics y tracking de conversiones</Text>
                  </li>
                </ul>
              </div>

              {/* Merchant action: manage the app in their own TN admin */}
              <div className="flex justify-center pt-2">
                <Button
                  appearance="neutral"
                  onClick={handleManageApp}
                >
                  <Icon source={<ExternalLinkIcon />} color="currentColor" />
                  Ver apps en mi panel de TiendaNube
                </Button>
              </div>

              {/* Staff-only sign-in — demoted so merchants don't read it as a required step */}
              <div className="border-t pt-4 text-center">
                <Text fontSize="caption">
                  ¿Sos del equipo de ECommerceLab?{' '}
                  <button
                    type="button"
                    onClick={handleLoginClick}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    Iniciá sesión con tu cuenta @ecommercelab.agency
                  </button>
                </Text>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}
